import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { FadeIn, Overline } from "../components/Motion";

const BG = "https://static.prod-images.emergentagent.com/jobs/2f486ce3-6005-42d6-adf3-873498688899/images/862cebbe85018cf9ecac49de64e03f68d204c9e05ee71ff93f070a708aa8b736.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", stage: "", message: "" };

const Contact = () => {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in name, email and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setDone(true);
            toast.success("Message received. We'll be in touch.");
            setForm(initial);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="page-contact" className="relative text-white min-h-screen">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.25]"
                style={{ backgroundImage: `url(${BG})` }}
                aria-hidden="true"
            />
            <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(180deg, rgba(5,7,10,0.85) 0%, rgba(5,7,10,0.95) 60%, #05070A 100%)" }}
                aria-hidden="true"
            />

            <section className="relative pt-40 lg:pt-48 pb-32 lg:pb-44">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
                    <div className="md:col-span-5">
                        <FadeIn>
                            <Overline className="mb-8">Book a conversation</Overline>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.98] tracking-tight">
                                Tell us where things <span className="italic text-accent">actually stand.</span>
                            </h1>
                        </FadeIn>
                        <FadeIn delay={0.1}>
                            <p className="mt-10 max-w-md text-[16px] lg:text-lg leading-relaxed text-white/70">
                                Share where the business stands, what's not moving, and what next quarter
                                needs to look like. We'll take it from there.
                            </p>
                        </FadeIn>
                        <FadeIn delay={0.18}>
                            <div className="mt-14 space-y-6">
                                <div>
                                    <p className="text-overline mb-2">Response time</p>
                                    <p className="text-[14px] text-white/70">Within 2 business days · Personal reply</p>
                                </div>
                                <div>
                                    <p className="text-overline mb-2">What happens next</p>
                                    <p className="text-[14px] text-white/70">A short call to understand the business and where Orion Road can move things.</p>
                                </div>
                            </div>
                        </FadeIn>
                    </div>

                    <div className="md:col-span-7">
                        <FadeIn delay={0.1}>
                            <div className="border border-white/[0.08] bg-[#080B11]/70 backdrop-blur-sm p-8 lg:p-14">
                                {done ? (
                                    <div data-testid="contact-success" className="py-16 text-center">
                                        <div className="inline-flex h-14 w-14 items-center justify-center border border-white/20">
                                            <Check size={22} className="text-white" />
                                        </div>
                                        <h2 className="mt-8 font-display text-4xl lg:text-5xl">Message received.</h2>
                                        <p className="mt-6 text-[14px] text-white/65 max-w-md mx-auto leading-relaxed">
                                            Thanks for reaching out. We'll review your note and respond personally.
                                            In the meantime, no decks required.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => setDone(false)}
                                            data-testid="contact-reset"
                                            className="mt-10 text-[12px] tracking-[0.26em] uppercase text-white/65 hover:text-white hover-line"
                                        >
                                            Send another
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
                                        <Field
                                            label="Name"
                                            id="name"
                                            value={form.name}
                                            onChange={update("name")}
                                            placeholder="Your full name"
                                            required
                                        />
                                        <Field
                                            label="Email"
                                            id="email"
                                            type="email"
                                            value={form.email}
                                            onChange={update("email")}
                                            placeholder="you@company.com"
                                            required
                                        />
                                        <Field
                                            label="Company"
                                            id="company"
                                            value={form.company}
                                            onChange={update("company")}
                                            placeholder="Company name (optional)"
                                        />

                                        <div>
                                            <label htmlFor="message" className="block text-overline mb-4">Where things stand</label>
                                            <textarea
                                                id="message"
                                                data-testid="contact-input-message"
                                                value={form.message}
                                                onChange={update("message")}
                                                rows={5}
                                                required
                                                placeholder="What's not moving? What does next quarter need to look like?"
                                                className="w-full bg-transparent border-b border-white/20 pb-4 text-white placeholder:text-white/30 focus:border-white focus:outline-none transition-colors resize-none text-[15px] leading-relaxed"
                                            />
                                        </div>

                                        <div className="flex flex-col sm:flex-row sm:items-center gap-6 pt-4">
                                            <button
                                                type="submit"
                                                data-testid="contact-submit"
                                                disabled={submitting}
                                                className="group inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 text-[12px] tracking-[0.26em] uppercase hover:bg-white hover:text-[#05070A] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                {submitting ? "Sending…" : "Send message"}
                                                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                            </button>
                                            <p className="text-[12px] tracking-[0.18em] uppercase text-white/40">
                                                No spam. Personal reply.
                                            </p>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>
        </main>
    );
};

const Field = ({ label, id, type = "text", value, onChange, placeholder, required }) => (
    <div>
        <label htmlFor={id} className="block text-overline mb-4">{label}{required && <span className="text-white/40"> *</span>}</label>
        <input
            id={id}
            data-testid={`contact-input-${id}`}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            className="w-full bg-transparent border-b border-white/20 pb-4 text-white placeholder:text-white/30 focus:border-white focus:outline-none transition-colors text-[15px]"
        />
    </div>
);

export default Contact;
